import type { ThemeConfig, HospitalBranding } from '../types/theme';
import apiClient from './apiClient';

// Hospital theme/branding loader. The backend exposes branding via the
// public system-settings endpoint (`/system-settings/frontend/hospital-config`),
// so we read from there instead of the historical `/api/hospital/theme` stub
// (which the backend never implemented and would 404).
//
// The backend now overlays tenant_brandings on top of the JSON
// hospital.config row, so this single endpoint returns the same fields
// the public auth surface uses (displayName, primaryColor, accentColor,
// logoUrl). That keeps the EMR sidebar and the auth pages in sync —
// before the overlay, the sidebar fell back to "MAIE Medical" because
// onboarding only wrote tenant_brandings while the EMR read JSON.
export const themeApi = {
  getThemeConfig: async (): Promise<{
    theme: ThemeConfig | null;
    themeDark: ThemeConfig | null;
    themeId: string | null;
    themeIdDark: string | null;
    branding: HospitalBranding;
    billingCurrency: string | null;
  }> => {
    try {
      const { data } = await apiClient.get<{
        success: boolean;
        data: {
          name?: { en?: string; ar?: string };
          billingCurrency?: string | null;
          branding?: {
            hospitalName?: string;
            tagline?: string;
            aiProvider?: string;
            primaryColor?: string;
            accentColor?: string;
            logoUrl?: string | null;
            logoDarkUrl?: string | null;
            faviconUrl?: string | null;
            themeId?: string;
            themeIdDark?: string;
            themeConfig?: ThemeConfig;
            themeConfigDark?: ThemeConfig;
            pdfTemplate?: string;
            pdfAccentColor?: string;
          };
        } | null;
      }>('/system-settings/frontend/hospital-config', {
        // Thread the visible host through as a query param. The Vite
        // dev proxy uses changeOrigin:true, which strips the
        // <slug>.localhost subdomain off the Host header before NestJS
        // sees it; without ?host= the backend resolves to the shared
        // host and returns data:null. Mirrors what
        // fetchTenantConfigForHost() does for /public/tenant-config.
        params:
          typeof window !== 'undefined'
            ? { host: window.location.host }
            : undefined,
      });

      const payload = data?.data ?? null;
      const b = payload?.branding ?? {};
      return {
        // Per-tenant billing currency, durable across devices. useTheme
        // seeds the currency cache from this so amounts never silently
        // fall back to the browser locale (en-GB → GBP).
        billingCurrency: payload?.billingCurrency ?? null,
        // Surface the saved palette so useTheme can apply it via
        // setCustomTheme. Without this, every device fell back to its
        // own local defaultTheme — which is why the OWNER's "Medical
        // Green" pick never followed other users into the app.
        theme: b.themeConfig ?? null,
        // Optional dark counterpart. NULL means the tenant hasn't
        // customised dark mode and useTheme falls back to the catalogue
        // default ('dark-mode' from src/config/themes.ts).
        themeDark: b.themeConfigDark ?? null,
        themeId: b.themeId ?? null,
        themeIdDark: b.themeIdDark ?? null,
        branding: {
          // Prefer tenant_brandings.displayName (b.hospitalName) → legacy
          // hospital.config name → platform default "MAIE". The
          // VITE_HOSPITAL_NAME env was a single-tenant relic that pinned
          // every download (PDFs, page titles) to whatever was baked into
          // the build, even when the tenant had set their own clinic name.
          hospitalName: b.hospitalName || payload?.name?.en || 'MAIE',
          tagline: b.tagline || 'Excellence in Healthcare',
          aiProvider: b.aiProvider || 'MAIE AI',
          logo: b.logoUrl || undefined,
          // Tab favicon — Root.route.tsx swaps the <link rel="icon">
          // tag whenever this changes, so each tenant's tab in the
          // browser carries their own mark.
          favicon: b.faviconUrl || undefined,
          primaryColor: b.primaryColor || undefined,
          accentColor: b.accentColor || undefined,
          // Clinic-wide PDF defaults — the PDF renderer reads these from the
          // theme store (a doctor's per-user pdf-prefs override them).
          pdfTemplate:
            b.pdfTemplate === 'editorial' ||
            b.pdfTemplate === 'letterhead' ||
            b.pdfTemplate === 'compact' ||
            b.pdfTemplate === 'minimal' ||
            b.pdfTemplate === 'preprinted'
              ? b.pdfTemplate
              : undefined,
          pdfAccentColor: b.pdfAccentColor || undefined,
        },
      };
    } catch {
      return {
        theme: null,
        themeDark: null,
        themeId: null,
        themeIdDark: null,
        billingCurrency: null,
        branding: {
          hospitalName: 'MAIE',
          tagline: 'Excellence in Healthcare',
          aiProvider: 'MAIE AI',
        },
      };
    }
  },
};
